import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './EditExam.css';

/* ==========================================================================
   CONSTANTS
   ========================================================================== */

const EXAM_API_URL = 'https://api.avtechbmt.cloud/api/exams';

const EMPTY_MCQ = () => ({
  questionText: '',
  type: 'mcq',
  options: ['', '', '', ''],
  correctOptionIndex: 0,
  codeSnippet: '',
  language: '',
  marks: 1,
});

const EMPTY_CODING = () => ({
  questionText: '',
  type: 'coding',
  initialCodeSnippet: '// Write your code here',
  expectedOutput: '',
  marks: 5,
});

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Auto / Plain text' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'csharp', label: 'C#' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'sql', label: 'SQL' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'bash', label: 'Bash / Shell' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
];

const toLocalDatetimeInputValue = (dateLike) => {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalDatetimeInputValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function EditExam({ examId, onBack }) {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const routeId = examId || params.id;
  const isCreateMode = !routeId || routeId === 'create';

  /* ---------- state ---------- */
  const [loading, setLoading] = useState(!isCreateMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [durationInMinutes, setDurationInMinutes] = useState(30);
  const [passingMarks, setPassingMarks] = useState(10);
  const [accessType, setAccessType] = useState('private');

  const [scheduledStartTime, setScheduledStartTime] = useState('');
  const [scheduledEndTime, setScheduledEndTime] = useState('');

  const [questions, setQuestions] = useState([]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  const [initialSnapshot, setInitialSnapshot] = useState('');

  /* ---------- bulk import ref ---------- */
  const bulkInputRef = useRef(null);

  /* ============================================================
     HELPERS
     ============================================================ */

  const getAuthHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token')}`,
    }),
    [token]
  );

  /* ============================================================
     FETCH (edit mode)
     ============================================================ */

  const fetchExamDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${EXAM_API_URL}/admin/${routeId}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const exam = data.data;
        setTitle(exam.title || '');
        setDescription(exam.description || '');
        setDurationInMinutes(exam.durationInMinutes || 30);
        setPassingMarks(exam.passingMarks || 0);
        setAccessType(exam.accessType || 'private');

        setScheduledStartTime(
          toLocalDatetimeInputValue(exam.scheduledStartTime)
        );
        setScheduledEndTime(toLocalDatetimeInputValue(exam.scheduledEndTime));

        const qs = (exam.questions || []).map((q) => ({
          ...q,
          codeSnippet: q.codeSnippet || '',
          language: q.language || '',
        }));

        setQuestions(qs);
        setActiveQuestionIndex(0);

        setInitialSnapshot(
          JSON.stringify({
            title: exam.title || '',
            description: exam.description || '',
            durationInMinutes: exam.durationInMinutes || 30,
            passingMarks: exam.passingMarks || 0,
            accessType: exam.accessType || 'private',
            scheduledStartTime: toLocalDatetimeInputValue(
              exam.scheduledStartTime
            ),
            scheduledEndTime: toLocalDatetimeInputValue(exam.scheduledEndTime),
            questions: qs,
          })
        );
      } else {
        setError(data.message || 'Failed to fetch exam details.');
      }
    } catch {
      setError('Error connecting to backend server.');
    } finally {
      setLoading(false);
    }
  }, [routeId, getAuthHeaders]);

  useEffect(() => {
    if (!isCreateMode && routeId) fetchExamDetails();
    else {
      setInitialSnapshot(
        JSON.stringify({
          title: '',
          description: '',
          durationInMinutes: 30,
          passingMarks: 10,
          accessType: 'private',
          scheduledStartTime: '',
          scheduledEndTime: '',
          questions: [],
        })
      );
      setLoading(false);
    }
  }, [routeId, isCreateMode, fetchExamDetails]);

  /* ============================================================
     DERIVED STATS
     ============================================================ */

  const stats = useMemo(() => {
    const total = questions.length;
    const mcqCount = questions.filter((q) => q.type === 'mcq').length;
    const codingCount = questions.filter((q) => q.type === 'coding').length;
    const snippetMcqCount = questions.filter(
      (q) => q.type === 'mcq' && q.codeSnippet && q.codeSnippet.trim()
    ).length;

    const totalMarks = questions.reduce(
      (sum, q) => sum + (Number(q.marks) || 0),
      0
    );
    const passingNum = Number(passingMarks) || 0;

    let scheduleError = '';
    if (scheduledStartTime && scheduledEndTime) {
      const start = new Date(scheduledStartTime);
      const end = new Date(scheduledEndTime);
      if (end <= start) {
        scheduleError = 'End time must be after start time.';
      } else {
        const windowMinutes = (end - start) / 60000;
        if (windowMinutes < Number(durationInMinutes)) {
          scheduleError = `Window (${windowMinutes.toFixed(
            0
          )} min) is shorter than exam duration (${durationInMinutes} min).`;
        }
      }
    } else if (
      (scheduledStartTime && !scheduledEndTime) ||
      (!scheduledStartTime && scheduledEndTime)
    ) {
      scheduleError = 'Please set both start and end times, or leave both blank.';
    }

    return {
      total,
      mcqCount,
      codingCount,
      snippetMcqCount,
      totalMarks,
      passingNum,
      invalidPassing: passingNum > totalMarks,
      scheduleError,
    };
  }, [
    questions,
    passingMarks,
    scheduledStartTime,
    scheduledEndTime,
    durationInMinutes,
  ]);

  /* ============================================================
     DIRTY TRACKING
     ============================================================ */

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        title,
        description,
        durationInMinutes,
        passingMarks,
        accessType,
        scheduledStartTime,
        scheduledEndTime,
        questions,
      }),
    [
      title,
      description,
      durationInMinutes,
      passingMarks,
      accessType,
      scheduledStartTime,
      scheduledEndTime,
      questions,
    ]
  );

  const isDirty = initialSnapshot && currentSnapshot !== initialSnapshot;

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  /* ============================================================
     NAVIGATION
     ============================================================ */

  const handleGoBack = () => {
    if (isDirty) {
      const ok = window.confirm(
        'You have unsaved changes. Leave without saving?'
      );
      if (!ok) return;
    }
    if (onBack) onBack();
    else navigate('/admin/exams');
  };

  /* ============================================================
     QUESTION HANDLERS
     ============================================================ */

  const addQuestion = (type) => {
    const newQ = type === 'mcq' ? EMPTY_MCQ() : EMPTY_CODING();
    setQuestions((prev) => {
      setActiveQuestionIndex(prev.length);
      return [...prev, newQ];
    });
  };

  const removeQuestion = (index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setActiveQuestionIndex((prev) => {
      if (prev > index) return prev - 1;
      if (prev === index) return Math.max(0, prev - 1);
      return prev;
    });
  };

  const moveQuestion = (index, direction) => {
    setQuestions((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setActiveQuestionIndex((prev) => {
      const target = prev + direction;
      if (target < 0 || target >= questions.length) return prev;
      return target;
    });
  };

  const updateQuestion = (index, field, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateOption = (qIndex, oIndex, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = [...(next[qIndex].options || [])];
      opts[oIndex] = value;
      next[qIndex] = { ...next[qIndex], options: opts };
      return next;
    });
  };

  const toggleSnippetMode = (index) => {
    setQuestions((prev) => {
      const next = [...prev];
      const q = next[index];
      const hasSnippet =
        (q.codeSnippet && q.codeSnippet.trim()) || (q.language && q.language);
      next[index] = hasSnippet
        ? { ...q, codeSnippet: '', language: '' }
        : { ...q, codeSnippet: '// Paste the program here', language: '' };
      return next;
    });
  };

  /* ============================================================
     BULK QUESTION IMPORT (local — merges into `questions` state)
     ============================================================ */

  const fileToQuestionArray = (parsed) => {
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.questions)) return parsed.questions;
    return null;
  };

  const normalizeImportedQuestion = (raw, idx) => {
    const type = raw.type || 'mcq';
    const base = {
      ...raw,
      type,
      marks: Number(raw.marks) > 0 ? Number(raw.marks) : 1,
    };

    if (type === 'mcq') {
      return {
        ...base,
        questionText: (raw.questionText || '').trim(),
        options:
          Array.isArray(raw.options) && raw.options.length >= 2
            ? raw.options.map((o) => String(o))
            : ['', '', '', ''],
        correctOptionIndex:
          Number.isInteger(raw.correctOptionIndex) &&
          raw.correctOptionIndex >= 0 &&
          raw.correctOptionIndex < (raw.options?.length || 0)
            ? raw.correctOptionIndex
            : 0,
        codeSnippet: typeof raw.codeSnippet === 'string' ? raw.codeSnippet : '',
        language: typeof raw.language === 'string' ? raw.language : '',
      };
    }

    if (type === 'coding') {
      return {
        ...base,
        questionText: (raw.questionText || '').trim(),
        initialCodeSnippet:
          typeof raw.initialCodeSnippet === 'string'
            ? raw.initialCodeSnippet
            : '// Write your code here',
        expectedOutput:
          typeof raw.expectedOutput === 'string' ? raw.expectedOutput : '',
      };
    }

    throw new Error(`Question ${idx + 1}: unknown type "${type}"`);
  };

  const validateImportedQuestion = (q, idx) => {
    const where = `Question ${idx + 1}`;
    if (!q.questionText) return `${where}: questionText is required`;
    if (q.type === 'mcq') {
      if (!Array.isArray(q.options) || q.options.length < 2)
        return `${where}: at least 2 options required`;
      if (q.options.some((o) => !String(o).trim()))
        return `${where}: all options must have text`;
      if (
        q.correctOptionIndex === undefined ||
        q.correctOptionIndex < 0 ||
        q.correctOptionIndex >= q.options.length
      )
        return `${where}: correctOptionIndex out of range`;
    }
    if (q.type === 'coding') {
      if (!q.expectedOutput || !q.expectedOutput.trim())
        return `${where}: expectedOutput is required for coding questions`;
    }
    return null;
  };

  const handleBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    // Reset input so picking the same file again still fires onChange
    e.target.value = '';
    if (!file) return;

    setError('');
    setSuccess('');

    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Bulk import only supports .json files.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('File too large (max 2 MB).');
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch (parseErr) {
      setError(`Invalid JSON: ${parseErr.message}`);
      return;
    }

    const rawArr = fileToQuestionArray(parsed);
    if (!rawArr || rawArr.length === 0) {
      setError(
        'File must contain a non-empty array, either as the root or under the "questions" key.'
      );
      return;
    }

    if (rawArr.length > 500) {
      setError(
        `Too many questions (${rawArr.length}). Max is 500 per import.`
      );
      return;
    }

    const normalized = [];
    const problems = [];

    rawArr.forEach((raw, idx) => {
      try {
        const q = normalizeImportedQuestion(raw, idx);
        const err = validateImportedQuestion(q, idx);
        if (err) problems.push(err);
        else normalized.push(q);
      } catch (err) {
        problems.push(err.message);
      }
    });

    if (problems.length > 0) {
      setError(
        `${problems.length} question(s) failed validation. Nothing was imported.\n` +
          problems.slice(0, 6).join('\n') +
          (problems.length > 6 ? `\n…and ${problems.length - 6} more.` : '')
      );
      return;
    }

    // Decide append vs replace
    let replace = false;
    if (questions.length > 0) {
      replace = window.confirm(
        `You already have ${questions.length} question(s).\n\n` +
          `OK = REPLACE all existing questions with the ${normalized.length} imported ones.\n` +
          `Cancel = APPEND the ${normalized.length} imported ones to the end.`
      );
    }

    if (replace) {
      setQuestions(normalized);
      setActiveQuestionIndex(0);
      setSuccess(
        `✅ Replaced all questions with ${normalized.length} imported question(s). Review and click Save.`
      );
    } else {
      setQuestions((prev) => [...prev, ...normalized]);
      setActiveQuestionIndex(questions.length); // jump to first imported
      setSuccess(
        `✅ Appended ${normalized.length} question(s). Review and click Save.`
      );
    }
  };

  const triggerBulkImport = () => bulkInputRef.current?.click();

  /* ============================================================
     Schedule presets
     ============================================================ */

  const applySchedulePreset = (preset) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate()
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

    if (preset === 'clear') {
      setScheduledStartTime('');
      setScheduledEndTime('');
      return;
    }

    if (preset === 'today') {
      const start = new Date(now);
      start.setHours(10, 0, 0, 0);
      const end = new Date(now);
      end.setHours(13, 0, 0, 0);
      setScheduledStartTime(fmt(start));
      setScheduledEndTime(fmt(end));
      return;
    }

    if (preset === 'tomorrow') {
      const start = new Date(now);
      start.setDate(start.getDate() + 1);
      start.setHours(10, 0, 0, 0);
      const end = new Date(now);
      end.setDate(end.getDate() + 1);
      end.setHours(13, 0, 0, 0);
      setScheduledStartTime(fmt(start));
      setScheduledEndTime(fmt(end));
      return;
    }

    if (preset === 'thisSaturday') {
      const day = now.getDay();
      const daysUntilSat = (6 - day + 7) % 7 || 7;
      const start = new Date(now);
      start.setDate(start.getDate() + daysUntilSat);
      start.setHours(10, 0, 0, 0);
      const end = new Date(start);
      end.setHours(13, 0, 0, 0);
      setScheduledStartTime(fmt(start));
      setScheduledEndTime(fmt(end));
      return;
    }
  };

  /* ============================================================
     SUBMIT
     ============================================================ */

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setError('');
    setSuccess('');

    if (!title.trim()) {
      setError('Exam title is required.');
      return;
    }
    if (questions.length === 0) {
      setError('Please add at least one question.');
      return;
    }

    const invalidMcq = questions.findIndex(
      (q) =>
        q.type === 'mcq' &&
        (!q.options || q.options.some((o) => !String(o).trim()))
    );
    if (invalidMcq !== -1) {
      setError(`Question ${invalidMcq + 1} has empty options.`);
      return;
    }

    const snippetNoLang = questions.findIndex(
      (q) =>
        q.type === 'mcq' &&
        q.codeSnippet &&
        q.codeSnippet.trim() &&
        !q.language
    );
    if (snippetNoLang !== -1) {
      const proceed = window.confirm(
        `Question ${
          snippetNoLang + 1
        } has a code snippet but no language selected. Continue anyway?`
      );
      if (!proceed) return;
    }

    if (stats.scheduleError) {
      setError(stats.scheduleError);
      return;
    }

    setSubmitting(true);

    const cleanQuestions = questions.map((q) => {
      if (q.type !== 'mcq') return q;
      return {
        ...q,
        codeSnippet: q.codeSnippet?.trim() ? q.codeSnippet : '',
        language: q.language || '',
      };
    });

    const payload = {
      title: title.trim(),
      description: description.trim(),
      durationInMinutes: Number(durationInMinutes),
      passingMarks: Number(passingMarks),
      accessType,
      scheduledStartTime: fromLocalDatetimeInputValue(scheduledStartTime),
      scheduledEndTime: fromLocalDatetimeInputValue(scheduledEndTime),
      questions: cleanQuestions,
    };

    const url = isCreateMode
      ? EXAM_API_URL
      : `${EXAM_API_URL}/${routeId}`;
    const method = isCreateMode ? 'POST' : 'PUT';

    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(
          isCreateMode
            ? '✅ Exam created successfully!'
            : '✅ Exam updated successfully!'
        );
        setInitialSnapshot(currentSnapshot);
        setTimeout(() => {
          if (onBack) onBack();
          else navigate('/admin/exams');
        }, 1200);
      } else {
        setError(data.message || 'Failed to save exam.');
      }
    } catch {
      setError('Network error saving exam.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     RENDER
     ============================================================ */

  if (loading) {
    return (
      <div className="edit-exam-container">
        <p className="edit-exam-state-text">Loading exam details…</p>
      </div>
    );
  }

  const activeQ = questions[activeQuestionIndex];
  const activeHasSnippet =
    activeQ &&
    activeQ.type === 'mcq' &&
    ((activeQ.codeSnippet && activeQ.codeSnippet.trim()) || activeQ.language);

  return (
    <div className="edit-exam-container">
      {/* ==================== HEADER ==================== */}
      <div className="edit-exam-header">
        <div>
          <span className="edit-exam-field-label">
            05 / Assessment Studio
          </span>
          <h2>{isCreateMode ? 'Create New Exam' : 'Edit Assessment'}</h2>
          <p className="edit-exam-header-sub">
            {isCreateMode
              ? 'Build a new assessment from scratch. Add MCQs, coding questions, and configure scoring.'
              : 'Modify exam settings, questions, and scoring rules.'}
          </p>
        </div>
        <button
          type="button"
          className="edit-exam-cta-secondary"
          onClick={handleGoBack}
        >
          ← Back to Exams
        </button>
      </div>

      {/* ==================== ALERTS ==================== */}
      {error && (
        <div className="edit-exam-alert-box edit-exam-alert-error">
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div className="edit-exam-alert-box edit-exam-alert-success">
          {success}
        </div>
      )}

      {/* ==================== LIVE SUMMARY ==================== */}
      <div className="edit-exam-summary">
        <Stat icon="🎯" label="Duration" value={`${durationInMinutes} min`} />
        <Stat icon="📝" label="Questions" value={stats.total} />
        <Stat icon="🅰️" label="MCQs" value={stats.mcqCount} tone="mcq" />
        <Stat
          icon="💻"
          label="Snippet MCQs"
          value={stats.snippetMcqCount}
          tone="snippet"
        />
        <Stat
          icon="🧩"
          label="Coding"
          value={stats.codingCount}
          tone="coding"
        />
        <Stat
          icon="🏆"
          label="Total Marks"
          value={stats.totalMarks}
          tone={stats.invalidPassing ? 'warning' : 'primary'}
        />
        <Stat
          icon="✅"
          label="Passing"
          value={stats.passingNum}
          tone={stats.invalidPassing ? 'warning' : 'primary'}
        />
      </div>

      {stats.invalidPassing && (
        <div className="edit-exam-alert-box edit-exam-alert-error">
          ⚠️ Passing marks ({stats.passingNum}) exceed total marks (
          {stats.totalMarks}). No student can pass this exam.
        </div>
      )}

      {/* ==================== MAIN FORM ==================== */}
      <form onSubmit={handleSave} className="edit-exam-form-container">
        {/* ---------- EXAM META ---------- */}
        <h3 className="edit-exam-section-title">📋 Exam Details</h3>

        <div className="edit-exam-form-grid-2">
          <div className="edit-exam-form-group">
            <label className="edit-exam-form-label">
              Exam Title<span className="req">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. MERN Stack Assessment"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="edit-exam-form-input"
              autoFocus
            />
          </div>

          <div className="edit-exam-form-group">
            <label className="edit-exam-form-label">Description</label>
            <input
              type="text"
              placeholder="Brief description or instructions…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="edit-exam-form-input"
            />
          </div>
        </div>

        {/* ---------- ACCESS TYPE ---------- */}
        <div className="edit-exam-form-group">
          <label className="edit-exam-form-label">
            Access Type<span className="req">*</span>
          </label>
          <div className="edit-exam-access-toggle">
            <button
              type="button"
              className={`edit-exam-access-option ${
                accessType === 'private' ? 'is-selected' : ''
              }`}
              onClick={() => setAccessType('private')}
            >
              <span className="edit-exam-access-icon">🔒</span>
              <div className="edit-exam-access-body">
                <strong>Private (Invite Only)</strong>
                <span>
                  Only candidates you invite by email can take this exam.
                </span>
              </div>
            </button>
            <button
              type="button"
              className={`edit-exam-access-option ${
                accessType === 'public' ? 'is-selected' : ''
              }`}
              onClick={() => setAccessType('public')}
            >
              <span className="edit-exam-access-icon">🌐</span>
              <div className="edit-exam-access-body">
                <strong>Public</strong>
                <span>
                  Any logged-in user on the platform can take this exam.
                </span>
              </div>
            </button>
          </div>
        </div>

        <div className="edit-exam-form-grid-4">
          <div className="edit-exam-form-group">
            <label className="edit-exam-form-label">
              Duration (min)<span className="req">*</span>
            </label>
            <input
              type="number"
              required
              min="1"
              value={durationInMinutes}
              onChange={(e) => setDurationInMinutes(e.target.value)}
              className="edit-exam-form-input"
            />
          </div>

          <div className="edit-exam-form-group">
            <label className="edit-exam-form-label">
              Passing Marks<span className="req">*</span>
            </label>
            <input
              type="number"
              required
              min="0"
              value={passingMarks}
              onChange={(e) => setPassingMarks(e.target.value)}
              className="edit-exam-form-input"
            />
          </div>

          <div className="edit-exam-form-group">
            <label className="edit-exam-form-label">Total Marks (auto)</label>
            <input
              type="text"
              value={stats.totalMarks}
              disabled
              className="edit-exam-form-input"
              style={{ opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>

          <div className="edit-exam-form-group">
            <label className="edit-exam-form-label">Questions (auto)</label>
            <input
              type="text"
              value={stats.total}
              disabled
              className="edit-exam-form-input"
              style={{ opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>
        </div>

        {/* ==========================================================
            SCHEDULED AVAILABILITY WINDOW
            ========================================================== */}
        <div className="edit-exam-schedule-section">
          <div className="edit-exam-schedule-header">
            <h3 className="edit-exam-section-title" style={{ margin: 0 }}>
              🗓️ Scheduled Availability Window
            </h3>
            <span className="edit-exam-schedule-hint">
              Optional — leave both blank to make the exam available whenever
              it is active.
            </span>
          </div>

          <div className="edit-exam-form-grid-2">
            <div className="edit-exam-form-group">
              <label className="edit-exam-form-label">
                Opens At (Start)
              </label>
              <input
                type="datetime-local"
                value={scheduledStartTime}
                onChange={(e) => setScheduledStartTime(e.target.value)}
                className="edit-exam-form-input"
              />
            </div>

            <div className="edit-exam-form-group">
              <label className="edit-exam-form-label">
                Closes At (End)
              </label>
              <input
                type="datetime-local"
                value={scheduledEndTime}
                onChange={(e) => setScheduledEndTime(e.target.value)}
                className="edit-exam-form-input"
              />
            </div>
          </div>

          <div className="edit-exam-schedule-presets">
            <span className="edit-exam-schedule-presets-label">
              Quick presets:
            </span>
            <button
              type="button"
              className="edit-exam-preset-btn"
              onClick={() => applySchedulePreset('today')}
            >
              Today 10 AM – 1 PM
            </button>
            <button
              type="button"
              className="edit-exam-preset-btn"
              onClick={() => applySchedulePreset('tomorrow')}
            >
              Tomorrow 10 AM – 1 PM
            </button>
            <button
              type="button"
              className="edit-exam-preset-btn"
              onClick={() => applySchedulePreset('thisSaturday')}
            >
              This Saturday 10 AM – 1 PM
            </button>
            <button
              type="button"
              className="edit-exam-preset-btn edit-exam-preset-clear"
              onClick={() => applySchedulePreset('clear')}
            >
              ✕ Clear
            </button>
          </div>

          {stats.scheduleError && (
            <div
              className="edit-exam-alert-box edit-exam-alert-error"
              style={{ marginTop: '0.75rem' }}
            >
              ⚠️ {stats.scheduleError}
            </div>
          )}

          {scheduledStartTime && scheduledEndTime && !stats.scheduleError && (
            <div className="edit-exam-schedule-preview">
              ✅ Available from{' '}
              <strong>
                {new Date(scheduledStartTime).toLocaleString()}
              </strong>{' '}
              to{' '}
              <strong>{new Date(scheduledEndTime).toLocaleString()}</strong>
            </div>
          )}
        </div>

        {/* ==========================================================
            QUESTIONS — SPLIT VIEW (rail + active editor)
            ========================================================== */}
        <div className="edit-exam-questions-header">
          <h3 className="edit-exam-questions-title">
            ❓ Questions
            <span className="count">{stats.total}</span>
          </h3>
        </div>

        <div className="edit-exam-questions-split">
          {/* -------- LEFT RAIL -------- */}
          <aside className="edit-exam-q-rail">
            <div className="edit-exam-q-rail-header">
              <span>Jump to question</span>
            </div>

            {questions.length === 0 ? (
              <div className="edit-exam-q-rail-empty">
                No questions yet.
              </div>
            ) : (
              <ul className="edit-exam-q-rail-list">
                {questions.map((q, idx) => {
                  const hasSnippet =
                    q.type === 'mcq' && q.codeSnippet && q.codeSnippet.trim();
                  const isActive = idx === activeQuestionIndex;
                  return (
                    <li
                      key={q._id || idx}
                      className={`edit-exam-q-rail-item ${
                        isActive ? 'is-active' : ''
                      }`}
                      onClick={() => setActiveQuestionIndex(idx)}
                    >
                      <span className="edit-exam-q-rail-num">Q{idx + 1}</span>
                      <span className="edit-exam-q-rail-type">
                        {q.type === 'mcq' ? '🅰️' : '💻'}
                      </span>
                      <span className="edit-exam-q-rail-text">
                        {q.questionText?.trim() ? (
                          q.questionText
                        ) : (
                          <em>(untitled)</em>
                        )}
                      </span>
                      {hasSnippet && (
                        <span
                          className="edit-exam-q-rail-dot"
                          title="Has snippet"
                        >
                          🧾
                        </span>
                      )}
                      <span className="edit-exam-q-rail-marks">
                        {q.marks}m
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="edit-exam-q-rail-actions">
              <button
                type="button"
                className="edit-exam-cta-secondary edit-exam-add-mcq"
                onClick={() => addQuestion('mcq')}
              >
                ＋ MCQ
              </button>
              <button
                type="button"
                className="edit-exam-cta-secondary edit-exam-add-coding"
                onClick={() => addQuestion('coding')}
              >
                ＋ Coding
              </button>

              {/* NEW: Bulk import */}
              <button
                type="button"
                className="edit-exam-cta-secondary edit-exam-bulk-import"
                onClick={triggerBulkImport}
                title="Import many questions from a .json file"
              >
                📁 Bulk Import
              </button>
              <input
                ref={bulkInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleBulkFileSelected}
                style={{ display: 'none' }}
              />

              {/* NEW: Download template */}
              <a
                className="edit-exam-template-link"
                href="/templates/question-template.json"
                download="question-template.json"
                title="Download a sample .json file you can fill in"
              >
                ⬇️ Download Template
              </a>
            </div>
          </aside>

          {/* -------- RIGHT PANE (active question editor) -------- */}
          <section className="edit-exam-q-editor">
            {!activeQ ? (
              <div className="edit-exam-empty-questions">
                <span className="icon">📝</span>
                <span>
                  No questions yet. Click &quot;＋ MCQ&quot;, &quot;＋
                  Coding&quot;, or &quot;📁 Bulk Import&quot; on the left to
                  get started.
                </span>
              </div>
            ) : (
              <div
                className={`edit-exam-question-card type-${activeQ.type} ${
                  activeHasSnippet ? 'has-snippet' : ''
                }`}
              >
                {/* ---------- HEADER ---------- */}
                <div className="edit-exam-question-header">
                  <div className="edit-exam-question-header-left">
                    <span className="edit-exam-q-badge">
                      Q{activeQuestionIndex + 1}
                    </span>
                    <span className="edit-exam-q-type-pill">
                      {activeQ.type === 'mcq' ? '🅰️ MCQ' : '💻 Coding'}
                    </span>
                    {activeHasSnippet && (
                      <span className="edit-exam-q-snippet-pill">
                        🧾 Snippet MCQ
                      </span>
                    )}
                  </div>

                  <div className="edit-exam-question-actions-inline">
                    <button
                      type="button"
                      className="edit-exam-icon-btn"
                      onClick={() => moveQuestion(activeQuestionIndex, -1)}
                      disabled={activeQuestionIndex === 0}
                      title="Move up"
                      aria-label="Move question up"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="edit-exam-icon-btn"
                      onClick={() => moveQuestion(activeQuestionIndex, 1)}
                      disabled={
                        activeQuestionIndex === questions.length - 1
                      }
                      title="Move down"
                      aria-label="Move question down"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      className="edit-exam-icon-btn danger"
                      onClick={() => removeQuestion(activeQuestionIndex)}
                      title="Remove question"
                      aria-label="Remove question"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* ---------- BODY ---------- */}
                <div className="edit-exam-question-body">
                  <div className="edit-exam-form-group">
                    <label className="edit-exam-form-label">
                      Question Statement<span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Type the question here…"
                      required
                      value={activeQ.questionText}
                      onChange={(e) =>
                        updateQuestion(
                          activeQuestionIndex,
                          'questionText',
                          e.target.value
                        )
                      }
                      className="edit-exam-form-input"
                    />
                  </div>

                  <div className="edit-exam-marks-row">
                    <label>Marks</label>
                    <input
                      type="number"
                      min="1"
                      value={activeQ.marks}
                      onChange={(e) =>
                        updateQuestion(
                          activeQuestionIndex,
                          'marks',
                          Number(e.target.value)
                        )
                      }
                      className="edit-exam-form-input edit-exam-input-small"
                    />
                    <span
                      style={{
                        fontSize: '0.78rem',
                        color: 'var(--ee-text-secondary)',
                      }}
                    >
                      points for this question
                    </span>
                  </div>

                  {/* ---------- MCQ ---------- */}
                  {activeQ.type === 'mcq' && (
                    <>
                      <div className="edit-exam-snippet-toggle-row">
                        <label className="edit-exam-snippet-toggle">
                          <input
                            type="checkbox"
                            checked={activeHasSnippet}
                            onChange={() =>
                              toggleSnippetMode(activeQuestionIndex)
                            }
                          />
                          <span>
                            🧾 Include a code snippet (predict-the-output
                            style)
                          </span>
                        </label>
                        {activeHasSnippet && (
                          <span className="edit-exam-snippet-hint">
                            Students will see this program above the options.
                          </span>
                        )}
                      </div>

                      {activeHasSnippet && (
                        <div className="edit-exam-snippet-block">
                          <div className="edit-exam-form-group">
                            <label className="edit-exam-form-label">
                              Programming Language (for highlighting)
                            </label>
                            <select
                              value={activeQ.language || ''}
                              onChange={(e) =>
                                updateQuestion(
                                  activeQuestionIndex,
                                  'language',
                                  e.target.value
                                )
                              }
                              className="edit-exam-form-input"
                              style={{ maxWidth: 260 }}
                            >
                              {LANGUAGE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="edit-exam-form-group">
                            <label className="edit-exam-form-label">
                              Code Snippet / Program
                            </label>
                            <textarea
                              rows="7"
                              value={activeQ.codeSnippet || ''}
                              onChange={(e) =>
                                updateQuestion(
                                  activeQuestionIndex,
                                  'codeSnippet',
                                  e.target.value
                                )
                              }
                              className="edit-exam-form-input edit-exam-code-input edit-exam-snippet-input"
                              placeholder={
                                '// Paste the program here.\n// e.g.\nlet a = 5;\nlet b = 10;\nconsole.log(a + b * 2);'
                              }
                              spellCheck="false"
                            />
                            <span
                              style={{
                                fontSize: '0.72rem',
                                color: 'var(--ee-text-muted)',
                              }}
                            >
                              Preserve newlines and indentation — they will
                              render exactly as shown.
                            </span>
                          </div>

                          {activeQ.codeSnippet &&
                            activeQ.codeSnippet.trim() && (
                              <div className="edit-exam-snippet-preview">
                                <div className="edit-exam-snippet-preview-label">
                                  Preview
                                </div>
                                <pre
                                  className={`edit-exam-snippet-preview-code ${
                                    activeQ.language
                                      ? `language-${activeQ.language}`
                                      : ''
                                  }`}
                                >
                                  <code>{activeQ.codeSnippet}</code>
                                </pre>
                              </div>
                            )}
                        </div>
                      )}

                      <label className="edit-exam-form-label">
                        Options — click the radio to mark the correct answer
                      </label>
                      <div className="edit-exam-options-grid">
                        {(activeQ.options || ['', '', '', '']).map(
                          (opt, oIdx) => (
                            <div
                              key={oIdx}
                              className={`edit-exam-option-row ${
                                activeQ.correctOptionIndex === oIdx
                                  ? 'is-correct'
                                  : ''
                              }`}
                              onClick={() =>
                                updateQuestion(
                                  activeQuestionIndex,
                                  'correctOptionIndex',
                                  oIdx
                                )
                              }
                            >
                              <input
                                type="radio"
                                name={`correct_${activeQuestionIndex}`}
                                checked={
                                  activeQ.correctOptionIndex === oIdx
                                }
                                onChange={() =>
                                  updateQuestion(
                                    activeQuestionIndex,
                                    'correctOptionIndex',
                                    oIdx
                                  )
                                }
                                className="edit-exam-form-radio"
                                aria-label={`Mark option ${
                                  oIdx + 1
                                } as correct`}
                              />
                              <span className="edit-exam-option-badge">
                                {String.fromCharCode(65 + oIdx)}
                              </span>
                              <input
                                type="text"
                                placeholder={`Option ${oIdx + 1}`}
                                required
                                value={opt}
                                onChange={(e) =>
                                  updateOption(
                                    activeQuestionIndex,
                                    oIdx,
                                    e.target.value
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                                className="edit-exam-form-input"
                              />
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}

                  {/* ---------- CODING ---------- */}
                  {activeQ.type === 'coding' && (
                    <div className="edit-exam-coding-fields-group">
                      <div className="edit-exam-form-group">
                        <label className="edit-exam-form-label">
                          Initial Code Starter
                        </label>
                        <textarea
                          rows="5"
                          value={activeQ.initialCodeSnippet || ''}
                          onChange={(e) =>
                            updateQuestion(
                              activeQuestionIndex,
                              'initialCodeSnippet',
                              e.target.value
                            )
                          }
                          className="edit-exam-form-input edit-exam-code-input"
                          placeholder="// Starter code the student sees"
                          spellCheck="false"
                        />
                      </div>
                      <div className="edit-exam-form-group">
                        <label className="edit-exam-form-label">
                          Expected Output
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Hello World"
                          value={activeQ.expectedOutput || ''}
                          onChange={(e) =>
                            updateQuestion(
                              activeQuestionIndex,
                              'expectedOutput',
                              e.target.value
                            )
                          }
                          className="edit-exam-form-input"
                        />
                        <span
                          style={{
                            fontSize: '0.72rem',
                            color: 'var(--ee-text-muted)',
                          }}
                        >
                          Used for auto-evaluation when the exam is submitted
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </form>

      {/* ==================== STICKY SAVE BAR ==================== */}
      <div className="edit-exam-sticky-save">
        <div className="edit-exam-sticky-save-info">
          {isDirty ? (
            <>
              <span className="edit-exam-unsaved-dot" />
              <span>
                <strong>Unsaved changes</strong> — {stats.total} question
                {stats.total === 1 ? '' : 's'} · {stats.totalMarks} marks
              </span>
            </>
          ) : (
            <span>
              {isCreateMode
                ? 'Fill in the details and click Create when ready.'
                : 'All changes saved.'}{' '}
              {stats.total} question{stats.total === 1 ? '' : 's'} ·{' '}
              {stats.totalMarks} marks
            </span>
          )}
        </div>
        <div className="edit-exam-sticky-save-actions">
          <button
            type="button"
            className="edit-exam-cta-secondary"
            onClick={handleGoBack}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="edit-exam-cta-primary"
            disabled={
              submitting ||
              (!isCreateMode && !isDirty) ||
              Boolean(stats.scheduleError)
            }
            onClick={handleSave}
          >
            {submitting
              ? '⏳ Saving…'
              : isCreateMode
                ? '＋ Create Exam'
                : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   SMALL HELPERS
   ========================================================================== */

function Stat({ icon, label, value, tone = 'primary' }) {
  return (
    <div className="edit-exam-stat">
      <span className={`edit-exam-stat-icon tone-${tone}`}>{icon}</span>
      <div className="edit-exam-stat-body">
        <span className="edit-exam-stat-label">{label}</span>
        <span className="edit-exam-stat-value">{value}</span>
      </div>
    </div>
  );
}